import { useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  getWeeksWithEligibleInvoices,
  getMissingWeeks,
  formatWeekName,
  type InvoiceForWeek,
} from "@/lib/paymentRunWeeks";
import type { PaymentRun } from "@/hooks/usePaymentRuns";

export interface AutoPaymentRunsResult {
  /** Number of new runs created in the last call */
  createdCount: number;
}

/**
 * Hook that auto-generates payment runs for any week that has eligible invoices
 * but no corresponding payment run yet.
 *
 * Call `ensureRunsForWeeks(invoices, runs, budget)` after invoices and runs are
 * both loaded.  The function is idempotent: it resolves the authenticated user
 * ID internally and only creates runs for weeks that are missing one.
 *
 * Returns:
 *   - `ensureRunsForWeeks` — the async function to call
 *   - `generating` ref — true while the function is running (for UI spinners)
 */
export function useAutoPaymentRuns(onRunCreated?: () => void) {
  const { toast } = useToast();
  const generatingRef = useRef(false);

  const ensureRunsForWeeks = useCallback(
    async (
      invoices: InvoiceForWeek[],
      runs: PaymentRun[],
      budget: number
    ): Promise<AutoPaymentRunsResult> => {
      if (generatingRef.current) return { createdCount: 0 };
      generatingRef.current = true;

      try {
        const weeksWithInvoices = getWeeksWithEligibleInvoices(invoices);
        if (weeksWithInvoices.length === 0) return { createdCount: 0 };

        const existingWeekStarts = runs.map((r) => r.week_start);
        const missingWeeks = getMissingWeeks(weeksWithInvoices, existingWeekStarts);
        if (missingWeeks.length === 0) return { createdCount: 0 };

        // Resolve the authenticated user ID once before inserting.  A NULL
        // created_by would make the DB unique constraint on (created_by, week_start)
        // a no-op (Postgres treats NULLs as non-equal), allowing duplicates.
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const userId = user?.id ?? null;

        let createdCount = 0;
        for (const weekStart of missingWeeks) {
          const name = formatWeekName(weekStart);
          // upsert with ignoreDuplicates=true emits ON CONFLICT DO NOTHING so
          // that concurrent renders or re-mounts that race past the generatingRef
          // guard can never produce duplicate rows.  The DB-level unique constraint
          // on (created_by, week_start) is the authoritative guard; this is the
          // application-level complement.
          const { error } = await supabase.from("payment_runs").upsert(
            {
              name,
              budget,
              pay_date: weekStart,
              status: "draft",
              week_start: weekStart,
              auto_generated: true,
              created_by: userId,
            },
            { onConflict: "created_by,week_start", ignoreDuplicates: true }
          );

          if (!error) {
            createdCount++;
          } else {
            console.warn("useAutoPaymentRuns: failed to create run for week", weekStart, error);
          }
        }

        if (createdCount > 0) {
          onRunCreated?.();
          toast({
            title: `${createdCount} payment run${createdCount > 1 ? "s" : ""} auto-generated`,
            description: `Draft runs created for ${createdCount} week${createdCount > 1 ? "s" : ""} with eligible invoices.`,
          });
        }

        return { createdCount };
      } finally {
        generatingRef.current = false;
      }
    },
    [toast, onRunCreated]
  );

  return { ensureRunsForWeeks, generatingRef };
}
